// flattenApplicationData.ts
// Utility to flatten nested application data for use as initialValues in error correction mode

import { courseInfoFields } from "../config/tuitionFormConfig";

// For each form field, list all possible API keys that could map to it
// Add index signature for TS
interface FieldMappingType {
  [key: string]: { section: string; keys: string[] };
}

export const FIELD_MAPPING: FieldMappingType = {
  // Step 4: School Info
  programField: { section: "schoolInfo", keys: ["programField", "progField"] },
  programType: { section: "schoolInfo", keys: ["programType", "progType"] },
  degreeName: {
    section: "schoolInfo",
    keys: ["degreeName", "progDegreeName", "courseDegreeName"],
  },
  termBegin: { section: "schoolInfo", keys: ["termBegin"] },
  termEnd: { section: "schoolInfo", keys: ["termEnd"] },
  applicationTerm: {
    section: "schoolInfo",
    keys: ["applicationTerm", "applTerm"],
  },
  applicationYear: {
    section: "schoolInfo",
    keys: ["applicationYear", "applYear", "termYear"],
  },
  schoolName: { section: "schoolInfo", keys: ["schoolName", "istSchoolName"] },
  schoolUrl: { section: "schoolInfo", keys: ["schoolUrl", "schoolWebsite"] },
  degreeCompletionYear: {
    section: "schoolInfo",
    keys: ["degreeCompletionYear", "degreeComplYear"],
  },
  fundingSource: { section: "schoolInfo", keys: ["fundingSource"] },

  // Step 5: Course Info
  courseName: { section: "courseInfo", keys: ["courseName"] },
  //   reimbursementType: { section: 'courseInfo', keys: ['reimbursementType', 'reimbType'] },
  courseDeliveryType: {
    section: "courseInfo",
    keys: ["courseDeliveryType", "courseDelyType"],
  },
  courseProgramType: {
    section: "courseInfo",
    keys: ["courseProgramType", "programType"],
  },
  courseStartDate: { section: "courseInfo", keys: ["courseStartDate"] },
  courseEndDate: { section: "courseInfo", keys: ["courseEndDate"] },
  courseCredit: { section: "courseInfo", keys: ["courseCredit"] },
  classSchoolName: {
    section: "courseInfo",
    keys: ["schoolName", "istSchoolName"],
  },
  originZipCode: { section: "courseInfo", keys: ["originZip"] },
  destinationZipCode: { section: "courseInfo", keys: ["destinationZip"] },

  //fee fields
  estimatedTuitionFee: {
    section: "courseInfo",
    keys: ["estimatedTuitionFee", "estTutionFee"],
  },
  estimatedBooksFee: {
    section: "courseInfo",
    keys: ["estimatedBooksFee", "estBooksFee"],
  },
  estimatedLabFee: {
    section: "courseInfo",
    keys: ["estimatedLabFee", "estLabFee"],
  },
  estimatedRegistrationFee: {
    section: "courseInfo",
    keys: ["estimatedRegistrationFee", "estRegistFee"],
  },
  estimatedOtherFee: {
    section: "courseInfo",
    keys: ["estimatedOtherFee", "estOtherFee"],
  },
};

export const flattenApplicationData = (
  data: any,
  dropdownOptions?: any,
): Record<string, any> => {
  const flattened = { ...data };

  // Always include classCount if courses are present
  if (Array.isArray(data?.courses)) {
    flattened.classCount = data.courses.length;
  }

  // Helper to get value from nested or flat structure
  function getFieldValue(
    mapping: { section: string; keys: string[] },
    data: any,
  ): any {
    // Check nested schoolInfo
    if (mapping.section === "schoolInfo" && data?.schoolInfo) {
      for (const key of mapping.keys) {
        if (
          data.schoolInfo[key] !== undefined &&
          data.schoolInfo[key] !== null
        ) {
          return data.schoolInfo[key];
        }
      }
    }
    // Check flat (top-level) keys
    for (const key of mapping.keys) {
      if (data[key] !== undefined && data[key] !== null) {
        return data[key];
      }
    }
    return undefined;
  }

  // For each form field, check all possible locations: nested, then flat
  Object.entries(FIELD_MAPPING).forEach(([formField, mapping]) => {
    const value = getFieldValue(mapping, data);
    if (value !== undefined) {
      flattened[formField] = value;
    }
  });

  // Also check for applicationYear at the root
  if (
    data?.schoolInfo?.applicationYear !== undefined &&
    data?.schoolInfo?.applicationYear !== null
  ) {
    flattened.applicationYear = data.schoolInfo.applicationYear;
  }
  if (data.applicationYear !== undefined && data.applicationYear !== null) {
    flattened.applicationYear = data.applicationYear;
  }

  // For the first course, use raw field names; for others, use _classN suffix
  if (Array.isArray(data?.courses)) {
    const requiredFields = courseInfoFields.map(
      (f: { name: string }) => f.name,
    );
    data.courses.forEach((course: Record<string, any>, idx: number) => {
      requiredFields.forEach((field: string) => {
        let value = getCourseFieldValue(field, course);
        if (field === "reimbursementType") {
          value = mapReimbursementType(value, dropdownOptions);
        }
        const key = idx === 0 ? field : `${field}_class${idx}`;
        if (
          value === null ||
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean"
        ) {
          flattened[key] = value ?? "";
        } else {
          // Always set the key, even if missing
          flattened[key] = "";
        }
      });
    });
  }

  // Helper to get course field value (moved to top-level)
  function getCourseFieldValue(
    field: string,
    course: Record<string, any>,
  ): any {
    const mapping = FIELD_MAPPING[field];
    if (mapping?.section === "courseInfo") {
      for (const key of mapping.keys) {
        if (course[key] !== undefined && course[key] !== null) {
          return course[key];
        }
      }
      return undefined;
    }
    return course[field];
  }

  // Helper to map reimbursementType code to display value (moved to top-level)
  function mapReimbursementType(value: any, dropdownOptions: any): any {
    if (
      dropdownOptions?.reimbursement_type &&
      value !== undefined &&
      value !== null
    ) {
      const found = dropdownOptions.reimbursement_type.find(
        (item: any) => String(item.code) === String(value),
      );
      // eslint-disable-next-line no-console
      console.log(
        "[flattenApplicationData] mapped reimbursementType:",
        found ? found.value : value,
      );
      return found ? found.value : value;
    }
    return value;
  }

  const feeFieldMap = [
    { api: "estTutionFee", form: "estimatedTuitionFee" },
    { api: "estBooksFee", form: "estimatedBooksFee" },
    { api: "estLabFee", form: "estimatedLabFee" },
    { api: "estRegistFee", form: "estimatedRegistrationFee" },
    { api: "estOtherFee", form: "estimatedOtherFee" },
    { api: "estTuitionFee", form: "estimatedTuitionFee" },
    { api: "estAirfareBaggageFee", form: "estimatedAirfareBaggageFee" },
    { api: "estAutoRentalFee", form: "estimatedAutoRentalFee" },
    { api: "estParkTollFee", form: "estimatedParkTollFee" },
    { api: "estPublicTransportFee", form: "estimatedPublicTransportFee" },
    { api: "estHotelFee", form: "estimatedHotelFee" },
    { api: "estMilesDollarValue", form: "estimatedMilesDollarValue" },
  ];
  feeFieldMap.forEach(({ api, form }) => {
    if (data[api] !== undefined && data[api] !== null) {
      flattened[form] = data[api];
    }
  });

  return flattened;
};
